import { z } from 'zod';
import { articleUnitSchema } from '../inventory/article.schema';
import { BOM_ITEM_TYPES, MAX_BOM_DEPTH } from './bom-tree';

/**
 * Engineering-Stückliste je Projekt (VB-26025) — die Liste, die heute in Excel
 * entsteht und aus der die Bedarfsliste (ADR 0080) abgeleitet wird.
 *
 * Nicht zu verwechseln mit `BomItem` aus der Fertigung (ADR 0008): das ist ein
 * Artikel-Kit am Stamm („diese Baugruppe besteht immer aus …"). Hier geht es um
 * die Stückliste EINES Projekts, die sich mit der Konstruktion ändert.
 */

/** Herkunft der Stückliste: SolidWorks-Export oder von Hand erfasst. */
export const engineeringBomKindSchema = z.enum(['THREE_D', 'TWO_D']);
export type EngineeringBomKind = z.infer<typeof engineeringBomKindSchema>;

export const engineeringBomStatusSchema = z.enum(['DRAFT', 'RELEASED']);
export type EngineeringBomStatus = z.infer<typeof engineeringBomStatusSchema>;

export const engineeringBomItemTypeSchema = z.enum(BOM_ITEM_TYPES);

/**
 * Obergrenze je Stückliste. Die Beispieldatei VB-26025-1 hat 290 Zeilen; die
 * Grenze fängt Importfehler ab, bevor sie eine Transaktion sprengen.
 */
export const MAX_ENGINEERING_BOM_ITEMS = 2000;

export const engineeringBomResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Projekt-Kennung (`Project.code`) als Snapshot für Listen — spart der UI den Zweit-Aufruf. */
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  name: z.string(),
  kind: engineeringBomKindSchema,
  status: engineeringBomStatusSchema,
  /** Herkunft des Imports; null bei von Hand angelegten Listen. */
  sourceFileName: z.string().nullable(),
  importedAt: z.string().datetime().nullable(),
  note: z.string().nullable(),
  /** Anzahl Zeilen (alle Typen) — für die Übersicht. */
  itemCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type EngineeringBomResponse = z.infer<typeof engineeringBomResponseSchema>;

/**
 * Eine Zeile, so wie sie gespeichert ist — **flach**, mit `parentItemId` als
 * Struktur. Positionsnummer und Gesamtmenge stehen bewusst NICHT drin: die
 * rechnet `buildBomTree` aus der Struktur. Eine gespeicherte Gesamtmenge wäre ab
 * der ersten Mengenänderung gelogen.
 */
export const engineeringBomItemResponseSchema = z.object({
  id: z.string().uuid(),
  bomId: z.string().uuid(),
  parentItemId: z.string().uuid().nullable(),
  position: z.number().int(),
  type: engineeringBomItemTypeSchema,
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  quantity: z.number().nullable(),
  /** Stückzahl bei Meterware — treibt das Befestigungsmaterial (Stufe C). */
  pieceCount: z.number().nullable(),
  unit: articleUnitSchema.nullable(),
  materialCode: z.string().nullable(),
  /** Werkstoff als Klartext (`SS316Ti / 1.4571`) — was der CAD-Export liefert. */
  materialText: z.string().nullable(),
  dimensions: z.string().nullable(),
  groupCode: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  note: z.string().nullable(),
});
export type EngineeringBomItemResponse = z.infer<typeof engineeringBomItemResponseSchema>;

export const createEngineeringBomSchema = z.object({
  projectId: z.string().uuid(),
  name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(200),
  kind: engineeringBomKindSchema.default('TWO_D'),
  note: z.string().trim().max(2000).nullish(),
});
export type CreateEngineeringBomInput = z.infer<typeof createEngineeringBomSchema>;

export const updateEngineeringBomSchema = z.object({
  name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(200).optional(),
  kind: engineeringBomKindSchema.optional(),
  status: engineeringBomStatusSchema.optional(),
  note: z.string().trim().max(2000).nullish(),
});
export type UpdateEngineeringBomInput = z.infer<typeof updateEngineeringBomSchema>;

/**
 * Eine Zeile im Entwurf. `key` ist die Kennung **innerhalb dieses Speicherns**:
 * bei bestehenden Zeilen die Id, bei neuen eine vom Editor vergebene. Kinder
 * verweisen über `parentKey` darauf — so kann eine neue Baugruppe im selben
 * Vorgang neue Kinder bekommen, ohne dass der Editor erst Ids abholen muss.
 */
export const engineeringBomItemDraftSchema = z.object({
  key: z.string().trim().min(1).max(64),
  /** Vorhandene Zeile; fehlt bei neuen. */
  id: z.string().uuid().nullish(),
  parentKey: z.string().trim().max(64).nullish(),
  type: engineeringBomItemTypeSchema,
  description: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(300),
  articleId: z.string().uuid().nullish(),
  articleNumber: z.string().trim().max(60).nullish(),
  quantity: z.number().nonnegative('Menge darf nicht negativ sein.').nullish(),
  pieceCount: z.number().positive('Die Stückzahl muss größer als 0 sein.').max(1_000_000).nullish(),
  unit: articleUnitSchema.nullish(),
  materialCode: z.string().trim().max(10).nullish(),
  materialText: z.string().trim().max(200).nullish(),
  dimensions: z.string().trim().max(200).nullish(),
  groupCode: z.string().trim().max(20).nullish(),
  supplierId: z.string().uuid().nullish(),
  supplierName: z.string().trim().max(200).nullish(),
  note: z.string().trim().max(2000).nullish(),
});
export type EngineeringBomItemDraft = z.infer<typeof engineeringBomItemDraftSchema>;

/**
 * Die ganze Stückliste in einem Vorgang speichern: eine Transaktion, ein
 * Audit-Eintrag. Zeilen, die nicht mehr in der Liste stehen, werden entfernt.
 *
 * Die Reihenfolge im Array ist die Anzeige-Reihenfolge; das Elternteil muss
 * **vor** seinen Kindern stehen. Das ist keine Schikane, sondern die einfachste
 * Zusicherung, dass der Entwurf zyklenfrei ist — geprüft wird sie hier, damit
 * der Server sie nicht rekursiv suchen muss.
 */
const bomItemsPayloadSchema = z.object({
  items: z
    .array(engineeringBomItemDraftSchema)
    .max(MAX_ENGINEERING_BOM_ITEMS, `Höchstens ${MAX_ENGINEERING_BOM_ITEMS} Zeilen je Stückliste.`),
});

/**
 * Die Baum-Prüfung. Als eigene Funktion, weil sie für JEDEN Weg gelten muss, auf
 * dem Zeilen hereinkommen — Speichern aus dem Editor und Import aus dem CAD-
 * Export. Ein zweiter Eingang mit schwächerer Prüfung wäre die Hintertür, durch
 * die ein kaputter Baum doch in die Datenbank kommt.
 */
function refineBomItems(value: { items: EngineeringBomItemDraft[] }, ctx: z.RefinementCtx): void {
  {
    const depthOf = new Map<string, number>();
    const seen = new Set<string>();
    const hasChildren = new Set<string>();

    value.items.forEach((item, index) => {
      const at = (field: string) => ({ path: ['items', index, field] as (string | number)[] });

      if (seen.has(item.key)) {
        ctx.addIssue({ code: 'custom', message: `Kennung ${item.key} kommt doppelt vor.`, ...at('key') });
        return;
      }

      // Die eigene Kennung zählt hier noch NICHT als bekannt — sonst wäre eine
      // Zeile ihr eigenes Elternteil ein gültiger Baum (und ein Zyklus).
      let depth = 0;
      if (item.parentKey) {
        if (seen.has(item.parentKey)) {
          hasChildren.add(item.parentKey);
          depth = (depthOf.get(item.parentKey) ?? 0) + 1;
          if (depth >= MAX_BOM_DEPTH) {
            ctx.addIssue({
              code: 'custom',
              message: `Mehr als ${MAX_BOM_DEPTH - 1} Unterebenen sind nicht vorgesehen.`,
              ...at('parentKey'),
            });
          }
        } else {
          ctx.addIssue({
            code: 'custom',
            message: `Elternteil ${item.parentKey} steht nicht vor dieser Zeile.`,
            ...at('parentKey'),
          });
        }
      }
      seen.add(item.key);
      depthOf.set(item.key, depth);

      // Abschnitt und Zusatztext sind Text — keine Menge, kein Artikel. Sonst
      // wandert eine Überschrift als Position in die Bedarfsliste.
      if (item.type !== 'POSITION') {
        if (item.quantity != null) {
          ctx.addIssue({ code: 'custom', message: 'Abschnitt und Zusatztext haben keine Menge.', ...at('quantity') });
        }
        if (item.pieceCount != null) {
          ctx.addIssue({
            code: 'custom',
            message: 'Abschnitt und Zusatztext haben keine Stückzahl.',
            ...at('pieceCount'),
          });
        }
        if (item.articleId || item.articleNumber) {
          ctx.addIssue({
            code: 'custom',
            message: 'Abschnitt und Zusatztext haben keinen Artikel.',
            ...at('articleNumber'),
          });
        }
        return;
      }
      if (item.quantity == null) {
        ctx.addIssue({ code: 'custom', message: 'Menge ist erforderlich.', ...at('quantity') });
      }
      // Ein Artikelbezug ohne Nummer wäre ein Verweis, den niemand lesen kann.
      if (item.articleId && !item.articleNumber?.trim()) {
        ctx.addIssue({ code: 'custom', message: 'Zu einem Artikel gehört die Artikelnummer.', ...at('articleNumber') });
      }
    });

    // Nur Positionen dürfen Kinder haben — eine Überschrift ist keine Baugruppe.
    value.items.forEach((item, index) => {
      if (item.type !== 'POSITION' && hasChildren.has(item.key)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Nur Positionen können Unterzeilen haben.',
          path: ['items', index, 'type'],
        });
      }
    });
  }
}

export const saveEngineeringBomItemsSchema = bomItemsPayloadSchema.superRefine(refineBomItems);
export type SaveEngineeringBomItemsInput = z.infer<typeof saveEngineeringBomItemsSchema>;

/**
 * Import einer Stückliste aus einem CAD-Export (Stufe 2). Dieselben Zeilen wie
 * beim Speichern plus die Herkunft — damit später nachvollziehbar bleibt, aus
 * welcher Datei der Stand kam.
 */
export const importEngineeringBomSchema = bomItemsPayloadSchema
  .extend({ sourceFileName: z.string().trim().min(1).max(260) })
  .superRefine(refineBomItems);
export type ImportEngineeringBomInput = z.infer<typeof importEngineeringBomSchema>;

/** Ergebnis des Speicherns — was tatsächlich passiert ist. */
export const saveEngineeringBomItemsResultSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  removed: z.number().int(),
});
export type SaveEngineeringBomItemsResult = z.infer<typeof saveEngineeringBomItemsResultSchema>;

/**
 * Übertrag der Stückliste in den Materialbedarf (ADR 0091 → ADR 0080).
 *
 * Ziel ist **entweder** eine neue Bedarfsliste (`name`) **oder** eine bestehende
 * (`listId`), deren Positionen ersetzt werden — genau eines von beidem, wie bei
 * der Projektauslegung (ADR 0081). „Anhängen" gibt es bewusst nicht: die
 * Stückliste ist die Wahrheit über den Bedarf, ein zweiter Lauf mit Anhängen
 * hätte jeden Artikel doppelt in der Liste.
 */
export const generateBomMaterialListSchema = z
  .object({
    /** Quell-Stückliste. Sie muss zum Projekt im Pfad gehören. */
    bomId: z.string().uuid(),
    /** Bestehende Bedarfsliste, deren Positionen ersetzt werden. */
    listId: z.string().uuid().optional(),
    /** Bezeichnung einer neu anzulegenden Bedarfsliste. */
    name: z.string().trim().min(1).max(120).optional(),
    /**
     * Zeilen mitnehmen, deren Nummer der Artikelstamm nicht kennt (Platzhalter
     * ohne Artikelbezug). Aus: nur Positionen mit echtem Artikel — sinnvoll,
     * wenn die Liste direkt bestellt werden soll.
     */
    includeWithoutArticle: z.boolean().default(true),
  })
  .refine((input) => Boolean(input.listId) !== Boolean(input.name), {
    message: 'Entweder eine bestehende Bedarfsliste wählen oder eine neue benennen.',
  });
export type GenerateBomMaterialListInput = z.infer<typeof generateBomMaterialListSchema>;

export const generateBomMaterialListResultSchema = z.object({
  listId: z.string().uuid(),
  listName: z.string(),
  /** Angelegte Positionen mit Artikelbezug. */
  articles: z.number().int().min(0),
  /** Angelegte Positionen ohne Artikelbezug (Nummer da, Stamm kennt sie nicht). */
  placeholders: z.number().int().min(0),
  /** Kumulierte Zeilen, aus denen keine Position wurde (Grund steht in `issues`). */
  skipped: z.number().int().min(0),
  /** Ersetzte Positionen der Zielliste (0 bei neuer Liste). */
  replaced: z.number().int().min(0),
  /** Was auffiel, in Klartext — dieselben Hinweise wie in der Vorschau. */
  issues: z.array(z.string()),
});
export type GenerateBomMaterialListResult = z.infer<typeof generateBomMaterialListResultSchema>;
